/**
 * Metric Transport Service
 *
 * Provides a unified interface for receiving real-time metric updates.
 * Attempts WebSocket (Socket.io) connection first, and automatically
 * falls back to HTTP polling when WebSocket is unavailable.
 *
 * Switches back to WebSocket when the connection is re-established.
 *
 * Validates: Requirements 22.6 (WebSocket fallback to HTTP polling)
 */

import { io, Socket } from 'socket.io-client';
import {
  POLLING,
  type MetricsUpdatePayload,
  type DBMetricsUpdatePayload,
  type AlertTriggeredPayload,
  type AlertResolvedPayload,
  type ServerStatusPayload,
} from 'shared';

// ─── Types ──────────────────────────────────────────────────────────────────

export type ConnectionState = 'connecting' | 'websocket' | 'polling' | 'disconnected';

export interface MetricTransportConfig {
  /** Base URL for the API (e.g., http://localhost:4000) */
  apiBaseUrl: string;
  /** JWT token for authentication */
  token: string;
  /** Company ID for constructing polling URLs */
  companyId: string;
  /** Polling interval in ms (default: POLLING.DEFAULT_INTERVAL_MS) */
  pollingIntervalMs?: number;
  /** WebSocket connect timeout in ms (default: POLLING.WS_CONNECT_TIMEOUT_MS) */
  wsConnectTimeoutMs?: number;
  /** Max WebSocket reconnect attempts before polling fallback (default: POLLING.WS_MAX_RECONNECT_ATTEMPTS) */
  wsMaxReconnectAttempts?: number;
}

export type MetricsUpdateHandler = (payload: MetricsUpdatePayload) => void;
export type DBMetricsUpdateHandler = (payload: DBMetricsUpdatePayload) => void;
export type AlertTriggeredHandler = (payload: AlertTriggeredPayload) => void;
export type AlertResolvedHandler = (payload: AlertResolvedPayload) => void;
export type ServerStatusHandler = (payload: ServerStatusPayload) => void;
export type ConnectionStateHandler = (state: ConnectionState) => void;

// ─── MetricTransport Class ──────────────────────────────────────────────────

export class MetricTransport {
  private config: Required<MetricTransportConfig>;
  private state: ConnectionState = 'disconnected';
  private socket: Socket | null = null;
  private subscribedServers: Set<string> = new Set();
  private pollingTimers: Map<string, ReturnType<typeof setInterval>> = new Map();
  private wsReconnectAttempts = 0;
  private wsConnectTimer: ReturnType<typeof setTimeout> | null = null;

  // Event handlers
  private metricsHandlers: MetricsUpdateHandler[] = [];
  private dbMetricsHandlers: DBMetricsUpdateHandler[] = [];
  private alertTriggeredHandlers: AlertTriggeredHandler[] = [];
  private alertResolvedHandlers: AlertResolvedHandler[] = [];
  private serverStatusHandlers: ServerStatusHandler[] = [];
  private stateHandlers: ConnectionStateHandler[] = [];

  constructor(config: MetricTransportConfig) {
    this.config = {
      ...config,
      pollingIntervalMs: config.pollingIntervalMs ?? POLLING.DEFAULT_INTERVAL_MS,
      wsConnectTimeoutMs: config.wsConnectTimeoutMs ?? POLLING.WS_CONNECT_TIMEOUT_MS,
      wsMaxReconnectAttempts: config.wsMaxReconnectAttempts ?? POLLING.WS_MAX_RECONNECT_ATTEMPTS,
    };
  }

  // ─── Public API ─────────────────────────────────────────────────────────

  /**
   * Initiates connection. Tries WebSocket first, falls back to polling
   * if WebSocket cannot be established within the configured timeout/attempts.
   */
  connect(): void {
    if (this.state !== 'disconnected') {
      return;
    }
    this.setState('connecting');
    this.connectWebSocket();
  }

  /**
   * Disconnects all transports and cleans up resources.
   */
  disconnect(): void {
    this.clearConnectTimer();
    this.stopPolling();

    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    this.wsReconnectAttempts = 0;
    this.setState('disconnected');
  }

  /**
   * Subscribe to a server's metric updates.
   * Works in both WebSocket and polling modes.
   */
  subscribe(serverId: string): void {
    if (this.subscribedServers.has(serverId)) {
      return;
    }

    this.subscribedServers.add(serverId);

    if (this.state === 'websocket' && this.socket) {
      this.socket.emit('subscribe:server', serverId);
    } else if (this.state === 'polling') {
      this.startPollingForServer(serverId);
    }
  }

  /**
   * Unsubscribe from a server's metric updates.
   */
  unsubscribe(serverId: string): void {
    if (!this.subscribedServers.has(serverId)) {
      return;
    }

    this.subscribedServers.delete(serverId);

    if (this.state === 'websocket' && this.socket) {
      this.socket.emit('unsubscribe:server', serverId);
    } else if (this.state === 'polling') {
      this.stopPollingForServer(serverId);
    }
  }

  /**
   * Returns the current connection state.
   */
  getConnectionState(): ConnectionState {
    return this.state;
  }

  // ─── Event Registration ─────────────────────────────────────────────────

  /**
   * Register a handler for metrics updates. Returns an unsubscribe function.
   */
  onMetricsUpdate(handler: MetricsUpdateHandler): () => void {
    this.metricsHandlers.push(handler);
    return () => {
      this.metricsHandlers = this.metricsHandlers.filter((h) => h !== handler);
    };
  }

  /**
   * Register a handler for DB metrics updates. Returns an unsubscribe function.
   */
  onDBMetricsUpdate(handler: DBMetricsUpdateHandler): () => void {
    this.dbMetricsHandlers.push(handler);
    return () => {
      this.dbMetricsHandlers = this.dbMetricsHandlers.filter((h) => h !== handler);
    };
  }

  /**
   * Register a handler for alert triggered events. Returns an unsubscribe function.
   */
  onAlertTriggered(handler: AlertTriggeredHandler): () => void {
    this.alertTriggeredHandlers.push(handler);
    return () => {
      this.alertTriggeredHandlers = this.alertTriggeredHandlers.filter((h) => h !== handler);
    };
  }

  /**
   * Register a handler for alert resolved events. Returns an unsubscribe function.
   */
  onAlertResolved(handler: AlertResolvedHandler): () => void {
    this.alertResolvedHandlers.push(handler);
    return () => {
      this.alertResolvedHandlers = this.alertResolvedHandlers.filter((h) => h !== handler);
    };
  }

  /**
   * Register a handler for server status changes. Returns an unsubscribe function.
   */
  onServerStatus(handler: ServerStatusHandler): () => void {
    this.serverStatusHandlers.push(handler);
    return () => {
      this.serverStatusHandlers = this.serverStatusHandlers.filter((h) => h !== handler);
    };
  }

  /**
   * Register a handler for connection state changes. Returns an unsubscribe function.
   */
  onConnectionStateChange(handler: ConnectionStateHandler): () => void {
    this.stateHandlers.push(handler);
    return () => {
      this.stateHandlers = this.stateHandlers.filter((h) => h !== handler);
    };
  }

  // ─── WebSocket Connection ───────────────────────────────────────────────

  private connectWebSocket(): void {
    const { apiBaseUrl, token, wsConnectTimeoutMs } = this.config;

    this.socket = io(apiBaseUrl, {
      auth: { token },
      transports: ['websocket'],
      reconnectionAttempts: this.config.wsMaxReconnectAttempts,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: wsConnectTimeoutMs,
    });

    // Set a connect timeout — if we don't connect in time, fall back to polling
    this.wsConnectTimer = setTimeout(() => {
      if (this.state === 'connecting') {
        this.handleWebSocketFailure();
      }
    }, wsConnectTimeoutMs);

    this.socket.on('connect', () => {
      this.clearConnectTimer();
      this.wsReconnectAttempts = 0;

      // If we were polling, stop polling and switch to WebSocket
      if (this.state === 'polling') {
        this.stopPolling();
      }

      this.setState('websocket');

      // Re-subscribe to all servers via WebSocket
      for (const serverId of this.subscribedServers) {
        this.socket!.emit('subscribe:server', serverId);
      }
    });

    this.socket.on('disconnect', (reason) => {
      // If the disconnect was initiated by the client (e.g., calling disconnect()),
      // don't attempt reconnection — Socket.io handles server-initiated disconnects.
      if (reason === 'io client disconnect') {
        return;
      }

      // Socket.io will attempt reconnection automatically based on config.
      // We track attempts to know when to fall back to polling.
    });

    this.socket.on('connect_error', () => {
      this.wsReconnectAttempts++;

      if (this.wsReconnectAttempts >= this.config.wsMaxReconnectAttempts) {
        this.handleWebSocketFailure();
      }
    });

    // Register metric event listeners
    this.socket.on('metrics:update', (payload: MetricsUpdatePayload) => {
      this.emitMetricsUpdate(payload);
    });

    this.socket.on('db-metrics:update', (payload: DBMetricsUpdatePayload) => {
      this.emitDBMetricsUpdate(payload);
    });

    this.socket.on('alert:triggered', (payload: AlertTriggeredPayload) => {
      this.emitAlertTriggered(payload);
    });

    this.socket.on('alert:resolved', (payload: AlertResolvedPayload) => {
      this.emitAlertResolved(payload);
    });

    this.socket.on('server:status', (payload: ServerStatusPayload) => {
      this.emitServerStatus(payload);
    });
  }

  /**
   * Called when WebSocket connection cannot be established or reconnection
   * attempts are exhausted. Switches to HTTP polling fallback.
   */
  private handleWebSocketFailure(): void {
    this.clearConnectTimer();

    // Disconnect the socket but keep the instance for potential reconnection
    if (this.socket) {
      this.socket.disconnect();
    }

    this.startPolling();
    this.setState('polling');

    // Attempt to reconnect WebSocket in the background after a delay
    this.scheduleWebSocketReconnect();
  }

  /**
   * Schedules a background WebSocket reconnection attempt.
   * If successful, polling will be stopped and WebSocket will take over.
   */
  private scheduleWebSocketReconnect(): void {
    // Try to reconnect after the polling interval
    const reconnectDelay = this.config.pollingIntervalMs * 3;

    setTimeout(() => {
      if (this.state !== 'polling') {
        return;
      }

      // Reset reconnect attempts and try again
      this.wsReconnectAttempts = 0;

      if (this.socket) {
        this.socket.connect();

        // Give it time to connect
        this.wsConnectTimer = setTimeout(() => {
          // If still polling after timeout, schedule another attempt
          if (this.state === 'polling') {
            this.scheduleWebSocketReconnect();
          }
        }, this.config.wsConnectTimeoutMs);
      }
    }, reconnectDelay);
  }

  // ─── HTTP Polling ───────────────────────────────────────────────────────

  /**
   * Starts HTTP polling for all currently subscribed servers.
   */
  private startPolling(): void {
    for (const serverId of this.subscribedServers) {
      this.startPollingForServer(serverId);
    }
  }

  /**
   * Stops all active polling timers.
   */
  private stopPolling(): void {
    for (const [serverId, timer] of this.pollingTimers) {
      clearInterval(timer);
      this.pollingTimers.delete(serverId);
    }
  }

  /**
   * Starts polling for a single server's metrics.
   */
  private startPollingForServer(serverId: string): void {
    if (this.pollingTimers.has(serverId)) {
      return;
    }

    // Poll immediately on start
    this.pollServerMetrics(serverId);

    // Then poll at the configured interval
    const timer = setInterval(() => {
      this.pollServerMetrics(serverId);
    }, this.config.pollingIntervalMs);

    this.pollingTimers.set(serverId, timer);
  }

  /**
   * Stops polling for a single server.
   */
  private stopPollingForServer(serverId: string): void {
    const timer = this.pollingTimers.get(serverId);
    if (timer) {
      clearInterval(timer);
      this.pollingTimers.delete(serverId);
    }
  }

  /**
   * Fetches the latest metrics for a server via the REST API.
   */
  private async pollServerMetrics(serverId: string): Promise<void> {
    const { apiBaseUrl, token, companyId } = this.config;
    const url = `${apiBaseUrl}/api/v1/companies/${companyId}/servers/${serverId}/metrics/latest`;

    try {
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        return;
      }

      const result = await response.json();

      if (result.success && result.data) {
        // Transform REST response into the same shape as WebSocket payload
        const latestData = result.data.latest ?? result.data.metrics ?? result.data;
        const payload: MetricsUpdatePayload = {
          serverId,
          metrics: latestData,
          processes: result.data.processes ?? [],
          timestamp: latestData.collectedAt ?? new Date().toISOString(),
        };

        this.emitMetricsUpdate(payload);
      }
    } catch {
      // Silently ignore polling errors — will retry on next interval
    }
  }

  // ─── Internal Event Emitters ────────────────────────────────────────────

  private emitMetricsUpdate(payload: MetricsUpdatePayload): void {
    for (const handler of this.metricsHandlers) {
      handler(payload);
    }
  }

  private emitDBMetricsUpdate(payload: DBMetricsUpdatePayload): void {
    for (const handler of this.dbMetricsHandlers) {
      handler(payload);
    }
  }

  private emitAlertTriggered(payload: AlertTriggeredPayload): void {
    for (const handler of this.alertTriggeredHandlers) {
      handler(payload);
    }
  }

  private emitAlertResolved(payload: AlertResolvedPayload): void {
    for (const handler of this.alertResolvedHandlers) {
      handler(payload);
    }
  }

  private emitServerStatus(payload: ServerStatusPayload): void {
    for (const handler of this.serverStatusHandlers) {
      handler(payload);
    }
  }

  // ─── Utilities ──────────────────────────────────────────────────────────

  private setState(newState: ConnectionState): void {
    if (this.state === newState) {
      return;
    }
    this.state = newState;
    for (const handler of this.stateHandlers) {
      handler(newState);
    }
  }

  private clearConnectTimer(): void {
    if (this.wsConnectTimer) {
      clearTimeout(this.wsConnectTimer);
      this.wsConnectTimer = null;
    }
  }
}
